import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import FormRenderer from '../shared/FormRenderer';
import { combineFormInstances } from '../../utils/formCombiner';
import { formAppliesToGrade } from '../../utils/formApplicability';
import { confirmUngeocodedMapAddressIfNeeded } from '../../utils/mapSubmitGuard';
import '../superadmin/AddDistrict.css';

const EditStudent = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [studentInfo, setStudentInfo] = useState({
    first_name: '',
    last_name: '',
    age: '',
    gender: '',
    grade: '',
    address: '',
    ethnicity_race: ''
  });
  const [formSubmissions, setFormSubmissions] = useState([]);
  const [availableForms, setAvailableForms] = useState([]);
  const [combinedFormDefinition, setCombinedFormDefinition] = useState(null);
  const [formInstanceMap, setFormInstanceMap] = useState({});
  const [formValues, setFormValues] = useState({});
  const [error, setError] = useState('');
  const invalidReported = useRef(false);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchStudentData();
  }, [id]);

  const fetchStudentData = async () => {
    try {
      setFetching(true);
      
      // Fetch student basic info
      const studentResponse = await axios.get(`/api/students/my-students/${id}`);
      const student = studentResponse.data;
      setStudentInfo({
        first_name: student.first_name || '',
        last_name: student.last_name || '',
        age: student.age || '',
        gender: student.gender || '',
        grade: student.grade || '',
        address: student.address || '',
        ethnicity_race: student.ethnicity_race || ''
      });

      // Fetch form submissions for this student
      const submissionsResponse = await axios.get(`/api/form-submissions/my-student/${id}`);
      const submissions = submissionsResponse.data;

      setFormSubmissions(submissions);

      /*
       * Only the forms this child actually owes.
       *
       * This screen offered every unsubmitted instance in the district, filtered by name for
       * helper forms and school lunch and by nothing else. Its sibling EditStudentForm.js was
       * given the grade rule; this one was missed, and it is the screen the send-back banner
       * and the new "a form is now required" banner both link to. The effect was real, not
       * theoretical: on the 2026-09-30 restore three Kindergarteners already held a Chromebook
       * 1:1 agreement that their grade did not call for and their own form list never showed
       * them. /api/form-instances/parent does not filter by grade server-side either, so it
       * has to happen here.
       */
      const availableFormsResponse = await axios.get('/api/form-instances/parent');
      const availableFormsData = (availableFormsResponse.data || []).filter((form) =>
        formAppliesToGrade(form, student.grade)
      );
      setAvailableForms(availableFormsData);

      // Create a map of submitted form instance IDs for quick lookup
      const submittedInstanceIds = new Set(submissions.map(s => s.form_instance_id));

      // Include both submitted forms and available forms that haven't been submitted yet
      // Filter out helper forms and school lunch forms - these should not be edited directly
      const allFormInstances = [
        ...submissions.filter(s =>
          !s.instance_name?.toLowerCase().includes('helper form') &&
          !s.master_form_name?.toLowerCase().includes('helper form') &&
          !s.instance_name?.toLowerCase().includes('school lunch') &&
          !s.master_form_name?.toLowerCase().includes('school lunch')
        ).map(s => ({ ...s, hasSubmission: true })),
        /*
         * availableFormsData, not the availableForms STATE.
         *
         * setAvailableForms was called a few lines up, but React state does not update until
         * the next render, and this effect runs once per :id - so this read was always the
         * previous value, which on first load is []. The result: no unsubmitted form has ever
         * appeared on this screen, and a parent sent here to add one saw only the forms they
         * had already sent. Found while checking that the new "a form is now required" banner
         * actually leads somewhere useful; it led to a page without the form on it.
         */
        ...availableFormsData.filter(f =>
          !submittedInstanceIds.has(f.id) &&
          !f.instance_name?.toLowerCase().includes('helper form') &&
          !f.master_form_name?.toLowerCase().includes('helper form') &&
          !f.instance_name?.toLowerCase().includes('school lunch') &&
          !f.master_form_name?.toLowerCase().includes('school lunch')
        ).map(f => ({ ...f, hasSubmission: false }))
      ];

      // Fetch form instance definitions for each form
      const formInstances = [];
      const combinedFormValues = {};

      for (const form of allFormInstances) {
        try {
          let instance;
          if (form.hasSubmission) {
            // This is a submitted form, fetch the instance
            const instanceResponse = await axios.get(`/api/form-instances/${form.form_instance_id}`);
            instance = instanceResponse.data;
          } else {
            // This is an available form that hasn't been submitted yet
            instance = form;
          }

          // Parse form_data if needed
          let formData = instance.form_data;
          if (typeof formData === 'string') {
            formData = JSON.parse(formData);
          }

          formInstances.push({
            id: form.hasSubmission ? form.form_instance_id : instance.id, // Use submission's instance ID for submitted forms
            instance_name: instance.instance_name,
            master_form_name: instance.master_form_name,
            form_data: formData,
            hasSubmission: form.hasSubmission
          });

          // Combine form values with instance ID prefix (only for submitted forms)
          if (form.hasSubmission) {
            const submissionData = form.form_data;
            if (submissionData && typeof submissionData === 'object') {
              Object.keys(submissionData).forEach(fieldName => {
                const prefixedName = `${form.form_instance_id}_${fieldName}`;
                combinedFormValues[prefixedName] = submissionData[fieldName];
              });
            }
          }
        } catch (err) {
          console.error(`Failed to fetch form instance ${form.id || form.form_instance_id}:`, err);
        }
      }

      // Combine all form instances
      const combined = combineFormInstances(formInstances);
      if (combined) {
        setCombinedFormDefinition(combined);
        setFormInstanceMap(combined.formInstanceMap);
        setFormValues(combinedFormValues);
      }
    } catch (error) {
      console.error('Failed to fetch student data:', error);
      setError('Failed to load student information');
    } finally {
      setFetching(false);
    }
  };

  const handleFieldChange = useCallback((newValues) => {
    setFormValues(newValues);
  }, []);

  const handleStudentInfoChange = (e) => {
    setStudentInfo({
      ...studentInfo,
      [e.target.name]: e.target.value
    });
  };

  /**
   * Every form this student has is rendered inside ONE <form>, so the browser validates all
   * of them together. When something is unanswered it blocks the submit, focuses that field
   * and scrolls to it - but it scrolls the field to the very bottom of the viewport, which is
   * exactly where the fixed Save Changes bar sits. The bar is 84px tall and the field is 49px,
   * so the field and the browser's own message end up completely behind it: the parent presses
   * Save, the page jumps, and nothing appears to happen.
   *
   * That mattered once forms could be sent back for corrections - a parent told to fix one
   * form could be silently blocked by an unanswered question on a different one.
   *
   * So take it over: suppress the hidden native bubble, say which field it is in the page's
   * own error style, and scroll it to the middle where it is actually visible.
   */
  const handleInvalid = (e) => {
    e.preventDefault();

    // onInvalid fires once per invalid control; only the first one matters here.
    if (invalidReported.current) return;
    invalidReported.current = true;
    setTimeout(() => { invalidReported.current = false; }, 0);

    const el = e.target;
    const wrapper = el.closest('.form-field, .form-group');
    const rawLabel = wrapper?.querySelector('label')?.innerText || '';
    // Labels carry a trailing required marker.
    const label = rawLabel.replace(/\s*\*\s*$/, '').trim();
    const section = el.closest('.form-section, .form-section-divider')
      ?.querySelector('.form-section-title, h3, h4')?.innerText?.trim();

    setError(
      label
        ? `Before this can be saved, “${label}”${section ? ` in ${section}` : ''} needs an answer. It has been scrolled into view below.`
        : 'One of the questions on this page still needs an answer before it can be saved.'
    );

    el.focus?.({ preventScroll: true });
    wrapper?.classList.add('needs-answer');

    // Centre it, because the fixed bar covers anything scrolled to the bottom edge.
    //
    // Deferred and instant, both deliberately: setting the message above re-renders the form,
    // which cancelled a smooth scroll started here and left the page at the top saying the
    // field had been scrolled to. Re-queried by name rather than reusing the node, since the
    // re-render can replace it.
    const fieldName = el.name;
    setTimeout(() => {
      const target = fieldName
        ? document.querySelector(`[name="${(window.CSS && CSS.escape) ? CSS.escape(fieldName) : fieldName}"]`)
        : null;
      const node = (target || el).closest('.form-field, .form-group') || target || el;
      node.scrollIntoView?.({ block: 'center' });
    }, 60);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    document.querySelectorAll('.needs-answer').forEach((n) => n.classList.remove('needs-answer'));
    setSaving(true);

    console.log('=== EDIT STUDENT SAVE START ===');
    console.log('Student ID:', id);
    console.log('Current formValues keys:', Object.keys(formValues));
    console.log('formSubmissions count:', formSubmissions.length);

    try {
      if (combinedFormDefinition && !confirmUngeocodedMapAddressIfNeeded(combinedFormDefinition, formValues)) {
        setSaving(false);
        return;
      }

      // Update student basic info
      await axios.put(`/api/students/my-students/${id}`, studentInfo);

      // Filter out helper forms and school lunch forms - these should not be edited directly
      const filteredAvailableForms = availableForms.filter(f =>
        !f.instance_name?.toLowerCase().includes('helper form') &&
        !f.master_form_name?.toLowerCase().includes('helper form') &&
        !f.instance_name?.toLowerCase().includes('school lunch') &&
        !f.master_form_name?.toLowerCase().includes('school lunch')
      );

      // Filter out helper forms and school lunch submissions as well
      const filteredFormSubmissions = formSubmissions.filter(s =>
        !s.instance_name?.toLowerCase().includes('helper form') &&
        !s.master_form_name?.toLowerCase().includes('helper form') &&
        !s.instance_name?.toLowerCase().includes('school lunch') &&
        !s.master_form_name?.toLowerCase().includes('school lunch')
      );

      // Create a map of submitted form instance IDs for quick lookup
      const submittedInstanceIds = new Set(filteredFormSubmissions.map(s => s.form_instance_id));

      // Include both submitted forms and available forms that haven't been submitted yet
      const allForms = [
        ...filteredFormSubmissions.map(s => ({ ...s, hasSubmission: true })),
        ...filteredAvailableForms.filter(f => !submittedInstanceIds.has(f.id)).map(f => ({ ...f, hasSubmission: false }))
      ];

      // Update each form submission or create new ones
      console.log('Processing', allForms.length, 'forms');
      for (const form of allForms) {
        const instanceId = form.hasSubmission ? form.form_instance_id : form.id;
        const submissionData = {};

        console.log('Processing form:', {
          instanceId,
          formName: form.instance_name || form.master_form_name,
          hasSubmission: form.hasSubmission,
          formId: form.id
        });

        // Extract fields for this instance
        Object.keys(formValues).forEach(prefixedName => {
          if (prefixedName.startsWith(`${instanceId}_`)) {
            const originalFieldName = prefixedName.replace(`${instanceId}_`, '');
            submissionData[originalFieldName] = formValues[prefixedName];
          }
        });

        console.log('Extracted submission data:', submissionData);

        // Check if this form has any data to save
        const hasData = Object.keys(submissionData).some(key => {
          const value = submissionData[key];
          return value !== null && value !== undefined && value !== '';
        });

        console.log('Form has data to save:', hasData);

        if (hasData) {
          if (form.hasSubmission) {
            // Update existing submission
            console.log('Updating existing submission:', form.id, 'with data:', submissionData);
            const updateResult = await axios.put(`/api/form-submissions/my-student-form/${form.id}`, {
              form_data: submissionData
            });
            console.log('Update result:', updateResult.data);
          } else {
            // Create new submission
            console.log('Creating new submission for instance:', instanceId, 'with data:', submissionData);
            const createResult = await axios.post('/api/form-submissions/submit', {
              form_instance_id: instanceId,
              form_data: submissionData,
              is_complete: true
            });
            console.log('Create result:', createResult.data);
          }
        }
      }

      console.log('=== EDIT STUDENT SAVE COMPLETE ===');
      navigate('/parent/students');
    } catch (error) {
      console.error('Failed to update student:', error);
      console.log('=== EDIT STUDENT SAVE FAILED ===');
      setError(error.response?.data?.error || 'Failed to update student information');
    } finally {
      setSaving(false);
    }
  };

  if (fetching) {
    return <div>Loading...</div>;
  }

  return (
    <div className="add-district">
      <h2>Edit Student Information</h2>
      {error && <div className="error-message">{error}</div>}
      <form onSubmit={handleSubmit} onInvalid={handleInvalid} className="student-edit-form">
        {/* Basic Student Info */}
        <div className="form-section-divider">
          <h3 className="form-section-title">Basic Information</h3>
          <div className="form-row">
            <div className="form-group">
              <label>First Name *</label>
              <input
                type="text"
                name="first_name"
                value={studentInfo.first_name}
                onChange={handleStudentInfoChange}
                required
              />
            </div>
            <div className="form-group">
              <label>Last Name *</label>
              <input
                type="text"
                name="last_name"
                value={studentInfo.last_name}
                onChange={handleStudentInfoChange}
                required
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Age</label>
              <input
                type="number"
                name="age"
                value={studentInfo.age}
                onChange={handleStudentInfoChange}
                min="0"
                max="25"
              />
            </div>
            <div className="form-group">
              <label>Gender</label>
              <select
                name="gender"
                value={studentInfo.gender}
                onChange={handleStudentInfoChange}
              >
                <option value="">Select...</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label>Grade</label>
            <input
              type="text"
              name="grade"
              value={studentInfo.grade}
              onChange={handleStudentInfoChange}
              placeholder="e.g., Kindergarten, 1st, 2nd, etc."
            />
          </div>

          <div className="form-group">
            <label>Address</label>
            <textarea
              name="address"
              value={studentInfo.address}
              onChange={handleStudentInfoChange}
              rows="3"
            />
          </div>

          <div className="form-group">
            <label>Ethnicity/Race</label>
            <input
              type="text"
              name="ethnicity_race"
              value={studentInfo.ethnicity_race}
              onChange={handleStudentInfoChange}
            />
          </div>
        </div>

        {/* Form Fields */}
        {combinedFormDefinition && combinedFormDefinition.sections && (
          <div className="form-section-divider">
            <h3 className="form-section-title">Form Information</h3>
            <FormRenderer
              formData={combinedFormDefinition}
              formValues={formValues}
              onChange={handleFieldChange}
              readOnly={false}
              hideFormYear
            />
          </div>
        )}

        {formSubmissions.length === 0 && (
          <div className="info-box">
            <p>No form submissions found for this student.</p>
          </div>
        )}

        {/* School Lunch Form Actions - REMOVED: School lunch applications are managed separately */}

        <div className="form-actions-fixed">
          <div className="form-actions-fixed-content">
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => navigate('/parent/students')}
            >
              Cancel
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default EditStudent;
